import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DailyWeatherForecast } from '../../../../../api/weather/models/daily-weather-forecast';
import { SiteManagerService } from '../../../../../api/site/site-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFormatService } from '../../../../../common/services/user-format.service';
import { WeatherIconInfo } from '../../../../../api/weather/models/weather-icon-info.model';
import { WeatherManagerService } from '../../../../../api/weather/weather-manager.service';

@UntilDestroy()
@Component({
    selector: 'toro-daily-forecast-card',
    templateUrl: './daily-forecast-card.component.html',
    styleUrls: ['./daily-forecast-card.component.less']
})
export class DailyForecastCardComponent implements OnInit {
    @HostBinding('class') class = 'toro-daily-forecast-card';

    private _isTomorrow = false;
    @Input() set isTomorrow(value: boolean) {
        if (this._isTomorrow === value) return;

        this._isTomorrow = value;
        this.setCardData();
    }

    get isTomorrow(): boolean {
        return this._isTomorrow;
    }

    private _dailyForecast: DailyWeatherForecast;
    @Input() set dailyForecast(value: DailyWeatherForecast) {
        if (this._dailyForecast === value) return;

        this._dailyForecast = value;
        this.setCardData();
    }

    get dailyForecast(): DailyWeatherForecast {
        return this._dailyForecast;
    }

    day: string = '--'
    tempUnits: string = '--';
    highTemp: string = '--';
    averageTemp: string = '--';
    lowTemp: string = '--';
    precipTotal: string = '--'
    windSpeed: string = '--';
    weatherIcon: WeatherIconInfo;

    // =========================================================================================================================================================
    // C'tor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private broadcastService: BroadcastService,
                private siteManager: SiteManagerService,
                private translateService: TranslateService,
                private userFormatService: UserFormatService,
                private weatherManager: WeatherManagerService,
    ) { }

    ngOnInit(): void {
        this.setCardData();

        this.broadcastService.userPreferencesChange
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.setCardData();
            });

    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private setCardData(): void {
        if (!this.dailyForecast) return;

        // Although the data is correct, when displaying the day (e.g., Sat) on the card, we need to adjust
        // for the day at the site. We can't simply use the local date.
        const dayAtSite = this.siteManager.getDayAtSiteFromUtcDate(this.dailyForecast.date);
        this.day = this.isTomorrow ? this.translateService.instant('STRINGS.TOMORROW') : dayAtSite;

        this.tempUnits = this.userFormatService.temperatureUnits;
        this.highTemp = <string>this.userFormatService.temperature(this.dailyForecast.highTemp, true);
        this.averageTemp = <string>this.userFormatService.temperature(this.dailyForecast.averageTemp, true);
        this.lowTemp = <string>this.userFormatService.temperature(this.dailyForecast.lowTemp, true);

        this.weatherIcon = this.weatherManager.getWeatherIcon(this.dailyForecast.conditions, this.dailyForecast.cloudCover);

        const userPrecip = this.userFormatService.rainfall(this.dailyForecast.precipTotal, true);
        this.precipTotal = `${userPrecip}${!this.userFormatService.isMetric ? '' : ' '}${this.userFormatService.rainfallUnits}`;
        const userWindSpeed = this.userFormatService.windSpeed(this.dailyForecast.windSpeed);
        this.windSpeed = `${userWindSpeed} ${this.userFormatService.windSpeedUnits}`;

    }
}
